import React, { Suspense, useEffect, useRef, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import { onMessage } from "firebase/messaging";
import { ThemeProvider } from "@mui/material/styles";
import { StyledEngineProvider } from "@mui/material/styles";

import "react-toastify/dist/ReactToastify.css";

import Router from "./routes/Router";

import "./App.css";
import "./colors.css";

import { LanguageProvider } from "./i18n";

import Loader from "@/components/Loader";
import IncomingCall from "@/components/VideoCall/CallingScreen/IncomingCall";
import Confirmation from "@/components/Confirmation";
import ScrollToTop from "@/components/ScrollToTop";
import PushNotificationAlert from "@/components/Chat/PushNotificationAlert";
import Overlays from "./Overlays";

import { IAPIStatusCode } from "@/utils/apis/APIEndpointConfig";
import { getAccessTokenApi } from "@/utils/apis/onboarding/OnBoardingApis";
import { storage } from "@/utils/Storage";
import { INotifyEnum, notify } from "@/utils/toaster";
import { usePermissionRestriction } from "@/utils/hooks/usePermissionRestriction";
import { useGooglePlaces } from "./utils/hooks/useGooglePlaces";
import { getLocation } from "./utils";

import incomingRingtone from "@/assets/incoming_ring.mp3";

import firebaseService from "@/firebaseConfig";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setCallerDetails,
  setCallingDetails,
  setFCMDeviceToken,
  videoAppointmentDetails,
} from "@/store/slices/videoAppointment";
import {
  rolePermission,
  setRoleAndPermission,
  setShowPermissionAlert,
} from "@/store/slices/permission";
import { chat, setMessageNotification } from "@/store/slices/chat";
import { fetchInAppNotification } from "@/store/slices/config";

import { IModalKeyEnums, IUserPermissionEnums } from "@/models/permission";
import { ICallType } from "./models/ApiRequestResponse/doctor";
import { IRoleType } from "./models/role";
import { ISessionKeys } from "./models";

import { theme } from "@/theme";

import { useNotificationNavigation } from "./utils/hooks/useNotificationNavigation";

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { callerDetails, callingDetails } = useAppSelector(
    videoAppointmentDetails
  );
  const { messageNotification } = useAppSelector(chat);
  const { showAlert } = useAppSelector(rolePermission);

  const { handleNotificationClick } = useNotificationNavigation();

  const { getAddressFromCoordinates } = useGooglePlaces({
    apiKey: import.meta.env.VITE_APP_GOOGLE_PLACES_API_KEY,
  });

  const currentUserId = storage.getSessionToken(ISessionKeys.USER_ID);
  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);

  const permissionRestrictionHandler = usePermissionRestriction();

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const getAccessTokenDetail = async () => {
    const isRefreshPagePermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.USER,
      IUserPermissionEnums.REFRESH_PAGE
    );
    if (!isRefreshPagePermissionGranted) return;
    const getAccessTokenRes = await getAccessTokenApi();
    if (
      getAccessTokenRes.statusCode === IAPIStatusCode.SUCCESS &&
      "result" in getAccessTokenRes
    ) {
      dispatch(setRoleAndPermission(getAccessTokenRes?.result?.permissions));
      storage.setSessionToken(
        ISessionKeys.USER_ID,
        getAccessTokenRes?.result?.user_id
      );
      storage.setSessionToken(
        ISessionKeys.ROLE_TYPE,
        getAccessTokenRes?.result?.role
      );
    }
  };

  useEffect(() => {
    (async () => {
      try {
        // Then generate FCM token
        const deviceToken = await firebaseService?.generateTokenFCM();
        console.log("deviceTokens", deviceToken);
        dispatch(setFCMDeviceToken(deviceToken));

        const location = getLocation();
        if (location) {
          await getAddressFromCoordinates({
            lat: location?.latitude,
            lng: location?.longitude,
          });
        }

        if (roleType !== IRoleType.GUEST) {
          dispatch(fetchInAppNotification({ page: 1 }));
        }
      } catch (error) {
        console.error("Error during initialization:", error);
      }
    })();
  }, []);

  // Create a reusable notification handler
  const handleNotificationPayload = useCallback(
    (payload, isBackground = false) => {
      const data = isBackground ? payload?.data : payload?.data;
      const isConversation = isBackground
        ? payload?.isConversation
        : !!payload?.data?.conversationId;

      if (!isConversation) {
        if (data?.type === ICallType.REJECTED) {
          dispatch(setCallerDetails(null));
          dispatch(
            setCallingDetails({
              ...callingDetails,
              callStatus: data?.type,
            })
          );
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
          notify(INotifyEnum.SUCCESS, payload?.notification?.body);
        } else if (data?.type === ICallType.CALL) {
          dispatch(setCallerDetails(data));
          if (!audioRef.current) {
            audioRef.current = new Audio(incomingRingtone);
            audioRef.current.loop = true;
          }
          audioRef.current.play().catch((err) => {
            console.warn("Could not play ringtone:", err);
          });
        }
      } else {
        console.log("payload", payload);
        dispatch(setMessageNotification(payload));
        handleNotificationClick(payload?.data?.event_type);
      }
    },
    [dispatch]
  );

  // Foreground notification handling
  useEffect(() => {
    getAccessTokenDetail();
    onMessage(firebaseService?.messaging, (payload) => {
      handleNotificationPayload(payload, false);
      console.log("🚀~Foreground message received:", payload);
    });

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [dispatch, handleNotificationPayload]);

  // Background notification handling
  useEffect(() => {
    const handleBackgroundMessage = (event: MessageEvent) => {
      if (event.data.type === "BACKGROUND_MESSAGE") {
        const payload = event.data.payload;
        handleNotificationPayload(payload, true);
        console.log("🚀 ~ handleBackgroundMessage ~ payload:--", payload);
      }
    };

    navigator.serviceWorker.addEventListener(
      "message",
      handleBackgroundMessage
    );

    return () => {
      navigator.serviceWorker.removeEventListener(
        "message",
        handleBackgroundMessage
      );
    };
  }, [dispatch, handleNotificationPayload]);

  useEffect(() => {
    if (!callerDetails && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [callerDetails]);

  return (
    <Suspense fallback={<Loader />}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <LanguageProvider>
            <ToastContainer />
            <ScrollToTop />
            <Router />
            <Overlays />
            {callerDetails?.type === ICallType.CALL && currentUserId && (
              <IncomingCall />
            )}
            {showAlert && (
              <Confirmation
                isOpen={showAlert}
                closeHandler={() => dispatch(setShowPermissionAlert())}
                showActionBtn={false}
                title="permission_access_title"
                alterMessage="permission_access"
                dontActionLabel="okay"
                qnsType={false}
              />
            )}
            {messageNotification &&
              messageNotification?.data?.senderId !== currentUserId && (
                <PushNotificationAlert />
              )}
            <div id="portal-root"></div>
          </LanguageProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </Suspense>
  );
};

export default App;
